import axios from '../axiosInstance';
import { Alert, AlertStatus, AlertApiResponse } from '../../containers/pages/CANsense/types/alerts';
import { getApiBaseUrl, getForwardedHost } from './apiClient';

export interface ExporterAlertsResponse {
  exporter: { id: number; display_name: string };
  alerts: Alert[];
  total: number;
}

/**
 * Prometheus alert states
 */
export enum PrometheusAlertState {
  FIRING = 'firing',
  PENDING = 'pending',
  INACTIVE = 'inactive'
}

/**
 * Prometheus alert severity levels
 */
export enum PrometheusAlertSeverity {
  CRITICAL = 'critical',
  WARNING = 'warning',
  INFO = 'info'
}

/**
 * Prometheus Alert interface - represents a single alert from Prometheus
 */
export interface PrometheusAlert {
  labels: {
    alertname: string;
    severity: string;
    [key: string]: string;
  };
  annotations: {
    summary?: string;
    description?: string;
    [key: string]: string | undefined;
  };
  state: PrometheusAlertState;
  activeAt?: string;
  value?: string;
  generatorURL?: string;
  fingerprint?: string;
}

/**
 * Transform PrometheusAlert to Alert format
 */
export const transformPrometheusAlert = (prometheusAlert: PrometheusAlert): Alert => {
  return {
    id: Math.abs(prometheusAlert.fingerprint?.substring(0, 8).split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0) || Math.floor(Math.random() * 1000000)),
    alertname: prometheusAlert.labels.alertname || 'Unknown Alert',
    severity: (prometheusAlert.labels.severity || 'info') as 'info' | 'warning' | 'critical',
    status: prometheusAlert.state === 'firing' ? 'new' :
            prometheusAlert.state === 'pending' ? 'acknowledged' : 'resolved' as 'new' | 'acknowledged' | 'resolved',
    summary: prometheusAlert.annotations.summary || prometheusAlert.labels.alertname || 'No summary available',
    description: prometheusAlert.annotations.description || 'No description available',
    starts_at: prometheusAlert.activeAt || new Date().toISOString(),
    created_at: prometheusAlert.activeAt || new Date().toISOString(),
    labels: JSON.stringify(prometheusAlert.labels),
    annotations: JSON.stringify(prometheusAlert.annotations),
    generator_url: prometheusAlert.generatorURL
  };
};

/**
 * Interface for alert action request bodies
 */
export interface AlertActionBody {
  id: number;
  suppress_minutes?: number;
}

/**
 * Interface for alert statistics
 */
export interface AlertStatistic {
  status: string;
  severity: string;
  count: number;
}

/**
 * Helper function to determine API URL based on environment
 */
const getApiUrl = (endpoint: string): string => {
  return `${getApiBaseUrl()}/sense${endpoint}`;
};

/**
 * Alert Service class - handles all alert-related API calls
 */
export class AlertService {
  async getAlertStatus(): Promise<AlertStatus> {
    const API_URL = getApiUrl('/getAlertStatus');
    const response = await axios.get(API_URL, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async getAlerts(active = true, limit = 50, offset = 0, includeSuppressed = false): Promise<AlertApiResponse> {
    const API_URL = getApiUrl('/getAlerts');
    const response = await axios.get(API_URL, {
      params: { active, limit, offset, include_suppressed: includeSuppressed || undefined },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async getAlert(id: number): Promise<Alert> {
    const API_URL = getApiUrl(`/getAlert/${id}`);
    const response = await axios.get(API_URL, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async acknowledgeAlert(id: number, suppressMinutes?: number): Promise<Alert> {
    const API_URL = getApiUrl('/acknowledgeAlert');
    const body: AlertActionBody = { id };
    if (suppressMinutes != null && suppressMinutes > 0) {
      body.suppress_minutes = suppressMinutes;
    }
    const response = await axios.post(API_URL, body, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async dismissAlert(id: number, suppressMinutes?: number): Promise<Alert> {
    const API_URL = getApiUrl('/dismissAlert');
    const body: AlertActionBody = { id };
    if (suppressMinutes != null && suppressMinutes > 0) {
      body.suppress_minutes = suppressMinutes;
    }
    const response = await axios.post(API_URL, body, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async deleteAlert(id: number, suppressMinutes?: number): Promise<{ message: string; alert: Alert }> {
    const API_URL = getApiUrl(`/deleteAlert/${id}`);
    const response = await axios.delete(API_URL, {
      data: suppressMinutes != null && suppressMinutes > 0
        ? { suppress_minutes: suppressMinutes }
        : undefined,
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async restoreAlert(id: number): Promise<Alert> {
    const API_URL = getApiUrl('/restoreAlert');
    const response = await axios.post(API_URL, { id }, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async markReviewed(id: number): Promise<Alert> {
    const API_URL = getApiUrl('/markReviewed');
    const response = await axios.post(API_URL, { id }, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async getAlertStats(): Promise<{ statistics: AlertStatistic[]; timestamp: Date }> {
    const API_URL = getApiUrl('/getAlertStats');
    const response = await axios.get(API_URL, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async getAlertsByExporter(
    exporterId: number,
    active = false,
    limit = 50,
    offset = 0
  ): Promise<ExporterAlertsResponse> {
    const API_URL = getApiUrl(`/exporters/${exporterId}/alerts`);
    const response = await axios.get(API_URL, {
      params: { active, limit, offset },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }

  async getMonitoringAlerts(sync?: boolean): Promise<{
    success: boolean;
    data: PrometheusAlert[];
    syncStatus?: string;
    timestamp: string;
  }> {
    const API_URL = `${getApiBaseUrl()}/monitoring/alerts`;

    const response = await axios.get(API_URL, {
      params: sync ? { sync: true } : undefined,
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return response.data?.data || response.data;
  }
}

// Export singleton instance
export const alertService = new AlertService();

// Export with original name for backward compatibility
export const alertApi = alertService;
